// Auth context — uses cookies via withCredentials.
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "./api";

const AuthContext = createContext({ user: undefined, login: async () => {}, logout: async () => {}, refresh: async () => {} });

/**
 * Routes that do NOT require a logged-in user and where we therefore should
 * NOT hit /api/auth/me on first render — that produces a noisy 401 in the
 * console for every anonymous landing-page visitor.
 *
 * Anything outside this list is treated as "may need auth" → /auth/me is
 * called silently on mount. A 401 there is normal and sets user = null.
 */
const PUBLIC_PATH_PREFIXES = [
    "/", // exact match handled below
    "/thank-you",
    "/datenschutz",
    "/impressum",
    "/legal/",
    "/radar/login", // Radar login is public — avoids a noisy /auth/me 401 on first paint
];

function isPublicPath(pathname) {
    if (pathname === "/" || pathname === "") return true;
    return PUBLIC_PATH_PREFIXES.some((p) => p !== "/" && pathname.startsWith(p));
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(undefined); // undefined = checking, null = anon, object = logged in

    const fetchMe = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            return data;
        } catch {
            // 401 here is normal for anonymous visitors — stay silent
            setUser(null);
            return null;
        }
    }, []);

    useEffect(() => {
        // Skip the /auth/me call on public routes — saves a request and avoids
        // a 401 in the browser console for every anonymous landing visitor.
        // Auth state stays `null` (anonymous). When the user navigates into
        // /admin/login or /login, those pages call `refresh()` themselves.
        const path = typeof window !== "undefined" ? window.location.pathname : "/";
        if (isPublicPath(path)) {
            setUser(null);
            return;
        }
        fetchMe();
    }, [fetchMe]);

    const login = useCallback(async (email, password) => {
        try {
            const { data } = await api.post("/auth/login", { email, password });
            setUser(data.user);
            return { ok: true, user: data.user };
        } catch (e) {
            return {
                ok: false,
                error: formatApiErrorDetail(e.response?.data?.detail) || e.message,
            };
        }
    }, []);

    const logout = useCallback(async () => {
        try {
            await api.post("/auth/logout");
        } catch {
            /* no-op */
        }
        setUser(null);
    }, []);

    const value = useMemo(
        () => ({ user, login, logout, refresh: fetchMe, setUser }),
        [user, login, logout, fetchMe],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    return useContext(AuthContext);
}
